import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { Cookie, Shield, BarChart3, Megaphone, Heart, Settings, Mail } from 'lucide-react';

export const CookiePolicy = () => {
  const { openSettings } = useCookieConsent();

  return (
    <div className="min-h-screen py-12 md:py-16 bg-[#F9F8F6]" data-testid="cookie-policy-page">
      <SEO
        title="Cookie Policy"
        description="Learn how Vintage Wardrobe Co uses cookies and how you can manage your preferences."
        path="/cookie-policy"
      />
      <div className="container-custom max-w-3xl">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-charcoal flex items-center justify-center">
              <Cookie className="w-6 h-6 text-[#C9A84C]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold" data-testid="cookie-policy-title">Cookie Policy</h1>
            </div>
          </div>
          <p className="text-softgrey text-sm md:text-base">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="space-y-10">
          {/* What Are Cookies */}
          <section data-testid="what-are-cookies-section">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-3">What Are Cookies?</h2>
            <p className="text-softgrey leading-relaxed text-sm md:text-base">
              Cookies are small text files that are placed on your device when you visit a website.
              They are widely used to make websites work more efficiently, provide a better browsing
              experience, and supply information to the site owners. Cookies allow us to recognise
              your device and remember certain information about your visit, such as your preferences
              and actions on the site.
            </p>
          </section>

          {/* Types of Cookies */}
          <section data-testid="types-of-cookies-section">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-6">Types of Cookies We Use</h2>
            <div className="space-y-4">
              {/* Essential */}
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5 md:p-6" data-testid="essential-cookies-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F7F5F2] flex items-center justify-center">
                    <Shield className="w-4.5 h-4.5 text-[#C9A84C]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-charcoal">Essential Cookies</h3>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#C9A84C]">Always Active</span>
                  </div>
                </div>
                <p className="text-softgrey text-sm leading-relaxed">
                  These cookies are necessary for the website to function and cannot be switched off.
                  They are usually set in response to actions you take, such as logging in, adding items
                  to your shopping cart, or filling in forms. Without these cookies, the site cannot
                  operate properly.
                </p>
              </div>

              {/* Analytics */}
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5 md:p-6" data-testid="analytics-cookies-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F7F5F2] flex items-center justify-center">
                    <BarChart3 className="w-4.5 h-4.5 text-[#C9A84C]" />
                  </div>
                  <h3 className="text-base font-bold text-charcoal">Analytics Cookies</h3>
                </div>
                <p className="text-softgrey text-sm leading-relaxed">
                  These cookies allow us to count visits and traffic sources so we can measure and
                  improve the performance of our site. They help us know which pages are the most
                  and least popular and see how visitors move around the site. All information these
                  cookies collect is aggregated and anonymous.
                </p>
              </div>

              {/* Marketing */}
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5 md:p-6" data-testid="marketing-cookies-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F7F5F2] flex items-center justify-center">
                    <Megaphone className="w-4.5 h-4.5 text-[#C9A84C]" />
                  </div>
                  <h3 className="text-base font-bold text-charcoal">Marketing Cookies</h3>
                </div>
                <p className="text-softgrey text-sm leading-relaxed">
                  These cookies may be set through our site by our advertising partners. They may
                  be used to build a profile of your interests and show you relevant adverts on other
                  sites. They do not store personal information directly, but are based on uniquely
                  identifying your browser and internet device.
                </p>
              </div>

              {/* Preferences */}
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5 md:p-6" data-testid="preferences-cookies-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F7F5F2] flex items-center justify-center">
                    <Heart className="w-4.5 h-4.5 text-[#C9A84C]" />
                  </div>
                  <h3 className="text-base font-bold text-charcoal">Preference Cookies</h3>
                </div>
                <p className="text-softgrey text-sm leading-relaxed">
                  These cookies enable the website to remember choices you make, such as your wishlist,
                  recently viewed products, and display preferences. They provide enhanced, more
                  personalised features to improve your shopping experience.
                </p>
              </div>
            </div>
          </section>

          {/* How to Control */}
          <section data-testid="control-cookies-section">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-3">How You Can Control Cookies</h2>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mb-4">
              You have the right to decide whether to accept or reject cookies. You can manage your
              cookie preferences at any time by using our Cookie Settings tool.
            </p>
            <button
              onClick={openSettings}
              className="inline-flex items-center gap-2 bg-charcoal hover:bg-black text-white font-bold text-xs uppercase tracking-widest px-6 py-3 rounded-lg transition-all duration-300"
              data-testid="cookie-policy-manage-btn"
            >
              <Settings className="w-4 h-4" />
              Manage Cookie Preferences
            </button>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mt-4">
              You can also control cookies through your browser settings. Most browsers allow you to
              refuse or accept cookies, delete existing cookies, and set preferences for certain websites.
              Please note that if you disable essential cookies, some parts of this website may not
              function properly.
            </p>
          </section>

          {/* Third-Party Cookies */}
          <section data-testid="third-party-cookies-section">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-3">Third-Party Cookies</h2>
            <p className="text-softgrey leading-relaxed text-sm md:text-base">
              Some cookies on our site are placed by third-party services that appear on our pages.
              These include payment processors (Stripe), analytics tools, and social media platforms.
              We do not control the cookies set by these third parties. Please refer to their respective
              privacy policies for information on how they use cookies.
            </p>
          </section>

          {/* Updates */}
          <section data-testid="policy-updates-section">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-3">Updates to This Policy</h2>
            <p className="text-softgrey leading-relaxed text-sm md:text-base">
              We may update this Cookie Policy from time to time to reflect changes in our practices
              or for other operational, legal, or regulatory reasons. We encourage you to review this
              page periodically for the latest information on our cookie practices.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-white border border-[#E2E2E2] rounded-xl p-6 md:p-8" data-testid="cookie-contact-section">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">Contact Us</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base">
              If you have any questions about our use of cookies, please{' '}
              <Link to="/contact" className="text-[#C9A84C] hover:underline font-semibold">contact us</Link>{' '}
              or email us at{' '}
              <a href="mailto:info@vintagewardrobe.co" className="text-[#C9A84C] hover:underline font-semibold">
                info@vintagewardrobe.co
              </a>.
            </p>
            <p className="text-softgrey text-sm mt-3">
              For more information about your privacy rights, please see our{' '}
              <Link to="/privacy-policy" className="text-[#C9A84C] hover:underline font-semibold">Privacy Policy</Link>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
